import React, { useCallback, useState } from 'react'

import type { CheckboxProps} from '@mui/material';
import { alpha, Checkbox, FormHelperText, styled, unstable_composeClasses } from '@mui/material'
import { Box } from '@mui/material'
import classnames from 'classnames'

import { Size } from '../../utils/types'
import FeatherIcon from '../FeatherIcon/FeatherIcon'
import { IconName } from '../FeatherIcon/FeatherIcon.types'
import type { ExtendField, FieldOverrideProps, SimpleFieldProps, SimpleFieldWrapperProps } from '../Field/Field.types'
import useAriaProps from '../Field/hooks/useAriaProps'
import SimpleFieldWrapper from '../SimpleFieldWrapper/SimpleFieldWrapper'
import simpleFieldWrapperClasses from '../SimpleFieldWrapper/simpleFieldWrapperClasses'
import { getCheckboxFieldUtilityClass } from './checkboxFieldClasses'

export type CheckBoxFieldVariant = 'standard' | 'outlined'

export interface CheckboxFieldProps extends SimpleFieldProps, ExtendField<CheckboxProps> {
  isGrouped?: boolean
  variant?: CheckBoxFieldVariant
}

const CheckboxFieldRoot = styled(SimpleFieldWrapper, {
  name: 'CheckboxField',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})<SimpleFieldWrapperProps & { variant?: CheckBoxFieldVariant }>(({ theme, variant }) => ({
  ...(variant === 'outlined' && {
    border: `1px solid ${theme.colours.baseLight}`,
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    marginLeft: 0,
    paddingRight: theme.spacing(1),

    '.Mui-checked.Mui-disabled > &': {
      backgroundColor: alpha(theme.colours.baseLight, 0.1),
    },

    '.Mui-checked:not(.Mui-disabled) > &': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.colours.primary, 0.1),
    },

    '&:hover:not(.Mui-disabled)': {
      borderColor: theme.palette.primary.main,

      [`& .${getCheckboxFieldUtilityClass('icon')}`]: {
        borderColor: theme.palette.primary.main,
      },
    },

    [`& .${getCheckboxFieldUtilityClass('label')}`]: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  }),

  ...(variant === 'standard' && {
    marginLeft: `-${theme.spacing(1)}`,
  }),

  [`& .${simpleFieldWrapperClasses.root}`]: {
    ...(variant === 'outlined' && {
      background: theme.palette.background.paper,
    }),
  },
}))

const CheckboxFieldCheckbox = styled<typeof Checkbox, keyof Omit<CheckboxProps, 'size'>>(Checkbox, {
  name: 'CheckboxField',
  slot: 'Checkbox',
  overridesResolver: (props, styles) => [styles.checkbox, props.size && styles.size],
})<FieldOverrideProps>(({ theme, size }) => ({
  ...(size === 'large' && {
    [`& .${getCheckboxFieldUtilityClass('icon')}`]: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      borderRadius: theme.display.borderRadius,
    },

    [`& .${getCheckboxFieldUtilityClass('iconChecked')}`]: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      borderRadius: theme.display.borderRadius,
    },
  }),
}))

const CheckboxFieldIcon = styled('span', {
  name: 'CheckboxField',
  slot: 'Icon',
  overridesResolver: (props, styles) => styles.icon,
})(({ theme }) => ({
  transition: 'all 0.1s ease-out',

  background: theme.colours.foreground,
  border: '1px solid',
  borderColor: theme.colours.baseLight,

  width: theme.spacing(2.4),
  height: theme.spacing(2.4),
  borderRadius: '0.3rem',
}))

const CheckboxFieldIconChecked = styled('span', {
  name: 'CheckboxField',
  slot: 'IconChecked',
  overridesResolver: (props, styles) => styles.iconChecked,
})(({ theme }) => ({
  transition: 'all 0.1s ease-out',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  background: theme.colours.primary,
  border: '1px solid',
  borderColor: theme.colours.primary,

  width: theme.spacing(2.4),
  height: theme.spacing(2.4),
  borderRadius: '0.3rem',

  '& svg': {
    color: theme.colours.baseExtraLight,
    stroke: theme.colours.baseExtraLight,
    strokeWidth: theme.spacing(0.5),
  },

  '&.Mui-disabled': {
    backgroundColor: theme.colours.primaryColorDisabled,
    borderColor: theme.colours.primaryColorDisabled,
  },
}))

const useUtilityClasses = (ownerState: Partial<CheckboxFieldProps>) => {
  const slots = {
    root: ['root', ownerState.size || 'medium', ownerState.checked ? 'checked' : ''],
    checkbox: ['checkbox'],
    label: ['label'],
    icon: ['icon'],
    iconChecked: ['iconChecked'],
    helperText: ['helperText'],
    errorText: ['errorText'],
  }

  return unstable_composeClasses(slots, getCheckboxFieldUtilityClass, ownerState.classes)
}

const _CheckboxField = (props: CheckboxFieldProps) => {
  const {
    className,
    classes,
    label,
    ['data-testid']: dataTestId,
    size = 'medium',
    WrapperProps,
    error,
    helperText,
    isGrouped,
    LabelProps,
    FormHelperTextProps,
    ErrorTextProps,
    variant = 'standard',
    responsive,
    tooltip,
    ...rootProps
  } = props

  const [checked, setChecked] = useState<boolean>(props.checked ?? false)
  const slotClasses = useUtilityClasses({ classes, size, checked: props.checked ?? checked })
  const { labelId, errorId, descriptionId, fieldAriaProps } = useAriaProps(props)
  const handleClick = useCallback(() => setChecked((value) => !value), [])

  const Container = isGrouped ? React.Fragment : Box

  // A Fragment takes no DOM props, so the test hook and class name go to the checkbox root
  // instead when grouped — both stay reachable, and React gets no props it can't apply.
  const containerAttributes = { 'data-testid': dataTestId, className: classnames(slotClasses.root, className) }

  const helperTextProps = helperText
    ? {
        ...FormHelperTextProps,
        id: descriptionId,
        className: classnames(slotClasses.helperText, FormHelperTextProps?.className),
      }
    : undefined

  const errorProps = error
    ? {
        ...ErrorTextProps,
        id: errorId,
        role: 'alert',
        className: classnames(slotClasses.errorText, ErrorTextProps?.className),
      }
    : undefined

  return (
    <Container {...(isGrouped ? {} : containerAttributes)}>
      <CheckboxFieldRoot
        {...WrapperProps}
        error={Boolean(error)}
        label={label}
        tooltip={tooltip}
        size={size}
        variant={variant}
        LabelProps={{ ...LabelProps, id: labelId, className: slotClasses.label }}
        responsive={responsive}
        control={
          <CheckboxFieldCheckbox
            {...rootProps}
            {...(isGrouped ? containerAttributes : {})}
            size={size}
            disableRipple
            icon={<CheckboxFieldIcon role="img" aria-label="checkbox icon" className={slotClasses.icon} />}
            checkedIcon={
              <CheckboxFieldIconChecked role="img" aria-label="checkbox icon" className={slotClasses.iconChecked}>
                <FeatherIcon name={IconName.Check} size={Size.Small} />
              </CheckboxFieldIconChecked>
            }
            inputProps={{
              ...rootProps.inputProps,
              ...fieldAriaProps,
              className: classnames(slotClasses.checkbox, className),
            }}
            onClick={handleClick}
          />
        }
      />

      {helperText &&
        (React.isValidElement(helperText) ? (
          React.cloneElement(helperText as React.ReactElement, helperTextProps)
        ) : (
          <FormHelperText {...helperTextProps}>{helperText}</FormHelperText>
        ))}

      {error &&
        (React.isValidElement(error) ? (
          React.cloneElement(error as React.ReactElement, errorProps)
        ) : (
          <FormHelperText {...errorProps} error>
            {error}
          </FormHelperText>
        ))}
    </Container>
  )
}

const CheckboxField = React.memo(_CheckboxField) as typeof _CheckboxField

export default CheckboxField
